<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="modal-backdrop-custom">
      <div 
        class="modal fade show d-block" 
        tabindex="-1" 
        role="dialog"
        aria-modal="true"
        @click.self="closeModal"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content border-0 shadow-lg rounded-3">
            
            <div class="modal-header border-bottom-0 pb-0">
              <h5 class="modal-title fw-bold text-dark">
                <i :class="isEditMode ? 'fas fa-edit' : 'fas fa-plus-circle'" class="text-primary me-2"></i>
                {{ isEditMode ? 'Editar Regional' : 'Nueva Regional' }}
              </h5>
              <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
            </div>

            <div class="modal-body p-4">
              <form @submit.prevent="handleSubmit">
                
                <div class="mb-3">
                  <label class="form-label fw-bold text-secondary small text-uppercase">Nombre</label>
                  <div class="input-group">
                    <span class="input-group-text bg-light border-end-0 text-muted">
                      <i class="fas fa-globe-americas"></i>
                    </span>
                    <input 
                      type="text" 
                      v-model="formData.name" 
                      class="form-control border-start-0" 
                      placeholder="Ej: Región Norte"
                      required 
                      autofocus
                    />
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label fw-bold text-secondary small text-uppercase">Código</label>
                  <div class="input-group">
                    <span class="input-group-text bg-light border-end-0 text-muted">
                      <i class="fas fa-barcode"></i>
                    </span>
                    <input 
                      type="text" 
                      v-model="formData.code" 
                      class="form-control border-start-0" 
                      placeholder="Ej: REG-01"
                      required 
                    />
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label fw-bold text-secondary small text-uppercase">Provincia</label>
                  <div class="input-group">
                    <span class="input-group-text bg-light border-end-0 text-muted">
                      <i class="fas fa-map-marker-alt"></i>
                    </span>
                    <select v-model="formData.province_id" class="form-select border-start-0" required>
                      <option value="" disabled selected>Seleccione una provincia...</option>
                      <option v-for="province in provinces" :key="province.id" :value="province.id">
                        {{ province.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="mb-4">
                  <label class="form-label fw-bold text-secondary small text-uppercase">Estado</label>
                  <div class="input-group">
                    <span class="input-group-text bg-light border-end-0 text-muted">
                      <i class="fas fa-toggle-on"></i>
                    </span>
                    <select v-model="formData.status" class="form-select border-start-0">
                      <option :value="1">Activo</option>
                      <option :value="0">Inactivo</option>
                    </select>
                  </div>
                </div>

                <div class="d-flex justify-content-end gap-2 pt-2 border-top">
                  <button type="button" class="btn btn-light border text-muted" @click="closeModal">
                    Cancelar
                  </button>
                  <button type="submit" class="btn btn-primary px-4 shadow-sm">
                    <i class="fas fa-save me-1"></i>
                    {{ isEditMode ? 'Actualizar' : 'Guardar' }}
                  </button>
                </div>

              </form>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch, toRefs } from 'vue';
import { createRegional, updateRegional } from '@/services/api/regionalService.js';
import { showLoadingAlert, closeLoadingAlert } from "@/utils/loadingAlert.js";
import userConfirmation from "@/utils/userConfirmation.js";
import Swal from "sweetalert2";

export default {
  name: 'ModalFormRegional',
  props: {
    regional: {
      type: Object,
      default: null,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    provinces: {
      type: Array,
      default: () => []
    },
  },
  emits: ['close', 'success'],
  setup(props, { emit }) {
    const { regional } = toRefs(props);
    const isEditMode = ref(false);
    
    const formData = ref({
      name: '',
      code: '',
      province_id: '',
      status: 1,
    });

    watch(
      regional,
      (newRegional) => {
        if (newRegional) {
          isEditMode.value = true;
          formData.value = { ...newRegional };
        } else {
          isEditMode.value = false;
          formData.value = { name: '', code: '', province_id: '', status: 1 };
        }
      },
      { immediate: true }
    );

    const handleSubmit = async () => {
      const actionText = isEditMode.value ? "actualizar" : "crear";
      
      const confirmed = await userConfirmation(
        `¿Confirma que desea ${actionText} esta regional?`
      );
      
      if (!confirmed) return;

      showLoadingAlert();
      
      try {
        if (isEditMode.value) {
          await updateRegional(props.regional.id, formData.value);
          Swal.fire('Éxito', 'Regional actualizada correctamente', 'success');
        } else {
          await createRegional(formData.value);
          Swal.fire('Éxito', 'Regional creada correctamente', 'success');
        }
        
        emit('success');
        emit('close'); 
      } catch (error) {
        console.error('Error submitting form:', error);
        Swal.fire('Error', error.response?.data?.message || 'Ocurrió un error al procesar la solicitud', 'error');
      } finally {
        closeLoadingAlert();
      }
    };

    const closeModal = () => {
      emit('close');
    };

    return {
      formData,
      isEditMode,
      handleSubmit,
      closeModal,
    };
  },
};
</script>

<style scoped>

.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 1050;
  transition: opacity 0.3s ease;
}

.input-group-text {
  background-color: #f8f9fa;
  border-color: #ced4da;
}
.form-control, .form-select {
  border-color: #ced4da;
  box-shadow: none;
}
.form-control:focus, .form-select:focus {
  border-color: #86b7fe;
  background-color: #fff;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-dialog,
.modal-fade-leave-active .modal-dialog {
  transition: transform 0.3s ease-out;
}

.modal-fade-enter-from .modal-dialog {
  transform: translateY(-20px);
}
.modal-fade-leave-to .modal-dialog {
  transform: translateY(-20px);
}
</style>